'use client';
import React, {
  useState,
  useMemo,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';

import { SelectedVariations } from '@components/views/tour/detail/context/types';
import { TourVariationViewModel } from '@api/index';
import { Guest } from '@components/views/tour/components/tour-selection/TourRoomGuest/props';

import { createGenericContext } from '../generateContext';

export type SelectedVariation = TourVariationViewModel & SelectedVariations;

export type TourRoomProps = {
  roomId: string;
  selectedVariationItems?: SelectedVariation[];
  selectedVariationIndex?: number;
  packageDetailId?: number;
  guests?: Guest;
};

export type TourPackageProps = {
  title?: string;
  dayCount?: number;
  nightCount?: number;
  packageDetailId: number;
  rooms?: TourRoomProps[];
};

type ContextInterface = {
  selectedVariationItems: SelectedVariation[];
  tourPackages?: TourPackageProps[];
  setTourPackages: Dispatch<SetStateAction<TourPackageProps[]>>;
  updatePackageRooms: (packageDetailId: number, rooms: TourRoomProps[]) => void;
  removePackageRoom: (packageDetailId: number, roomId: string) => void;
  addPackageRoom: (packageDetailId: number, room: TourRoomProps) => void;
  resetPackageRoomVariations: (packageDetailId: number, roomId: string) => void;
  removePackageRooms: (packageDetailId: number) => void;
  allAddedRoomsSelected?: boolean;
  addPackageRoomVariation: (
    packageDetailId: number,
    roomId: string,
    variation: SelectedVariation
  ) => void;
};

const [useTourDetailVariationContext, TourDetailContextProvider] =
  createGenericContext<ContextInterface>();

const TourDetailVariationProvider = (
  props: React.PropsWithChildren<unknown>
): React.JSX.Element => {
  const [tourPackages, setTourPackages] = useState<TourPackageProps[]>([]);

  // ===================================
  //  Update package rooms
  // ===================================
  const updatePackageRooms = useCallback(
    (packageDetailId: number, rooms: TourRoomProps[]) => {
      setTourPackages((current) =>
        current?.map((packageItem) =>
          packageItem.packageDetailId === packageDetailId
            ? {
                ...packageItem,
                rooms,
              }
            : packageItem
        )
      );
    },
    []
  );

  // ===================================
  //  Update package rooms
  // ===================================
  const removePackageRooms = useCallback((packageDetailId: number) => {
    setTourPackages((current) =>
      current?.map((packageItem) =>
        packageItem.packageDetailId === packageDetailId
          ? {
              ...packageItem,
              rooms: [],
            }
          : packageItem
      )
    );
  }, []);

  // ===================================
  //  Remove package room
  // ===================================
  const removePackageRoom = useCallback(
    (packageDetailId: number, roomId: string) => {
      setTourPackages((current) =>
        current?.map((packageItem) =>
          packageItem.packageDetailId === packageDetailId
            ? {
                ...packageItem,
                rooms: packageItem.rooms?.filter(
                  (room) => room.roomId !== roomId
                ),
              }
            : packageItem
        )
      );
    },
    []
  );

  // ===================================
  //  Add package room
  // ===================================
  const addPackageRoom = useCallback(
    (packageDetailId: number, room: TourRoomProps) => {
      setTourPackages((current) =>
        current?.map((packageItem) =>
          packageItem.packageDetailId === packageDetailId
            ? {
                ...packageItem,
                rooms: [...(packageItem.rooms || []), room],
              }
            : packageItem
        )
      );
    },
    []
  );

  // ===================================
  //  Add package room variation
  // ===================================
  const addPackageRoomVariation = useCallback(
    (packageDetailId: number, roomId: string, variation: SelectedVariation) => {
      setTourPackages((current) =>
        current?.map((packageItem) =>
          packageItem.packageDetailId === packageDetailId
            ? {
                ...packageItem,
                rooms: packageItem.rooms?.map((item) =>
                  item.roomId === roomId
                    ? {
                        ...item,
                        selectedVariationIndex: variation.variationIndex,
                        selectedVariationItems: [variation],
                      }
                    : item
                ),
              }
            : packageItem
        )
      );
    },
    []
  );

  // ===================================
  //  Reset package room variations
  // ===================================
  const resetPackageRoomVariations = useCallback(
    (packageDetailId: number, roomId: string) => {
      setTourPackages((current) =>
        current?.map((packageItem) =>
          packageItem.packageDetailId === packageDetailId
            ? {
                ...packageItem,
                rooms: packageItem.rooms?.map((item) =>
                  item.roomId === roomId
                    ? {
                        ...item,
                        selectedVariationIndex: undefined,
                        selectedVariationItems: [],
                      }
                    : item
                ),
              }
            : packageItem
        )
      );
    },
    []
  );

  // ===================================
  //  Generate selected variations from packages
  // ===================================
  const selectedVariationItems = useMemo(() => {
    const variations: SelectedVariation[] = [];

    tourPackages?.map((packageItem) =>
      packageItem?.rooms?.map((item) =>
        item?.selectedVariationItems?.map((variation) =>
          variations.push(variation)
        )
      )
    );

    return variations;
  }, [tourPackages]);

  // ===================================
  //  Check if all added rooms are selected
  // ===================================
  const allAddedRoomsSelected = useMemo(() => {
    const addedRooms: TourRoomProps[] = [];

    tourPackages?.map((packageItem) =>
      packageItem?.rooms?.map((item) => addedRooms.push(item))
    );

    return addedRooms?.every(
      (room) =>
        room?.selectedVariationItems?.length &&
        room?.selectedVariationItems?.length > 0
    );
  }, [tourPackages]);

  return (
    <TourDetailContextProvider
      value={{
        selectedVariationItems,

        tourPackages,
        setTourPackages,
        updatePackageRooms,
        removePackageRoom,
        addPackageRoom,
        addPackageRoomVariation,
        resetPackageRoomVariations,
        allAddedRoomsSelected,
        removePackageRooms,
      }}
    >
      {props.children}
    </TourDetailContextProvider>
  );
};

export { useTourDetailVariationContext, TourDetailVariationProvider };
